import { useState } from 'react'
import { Check, Plus, Search } from 'lucide-react'
import { useStudentsList } from '../../school_panel/api/hooks'
import { usePagination } from '../../school_panel/lib/usePagination'
import { Initials, PagedFooter } from '../../school_panel/components/ui'

export interface IndividualStudentPickerProps {
    gradeId: string;
    selectedIds: number[];
    onToggle: (id: number) => void;
    /** Grade name for the search placeholder ("Find students in Grade 10…"). */
    gradeLabel?: string;
    /** Only students of these sections (a teacher's own classes). */
    sectionIds?: number[] | null;
}

/** Design `.field` search + `.row` list with `.mini` Add / Added buttons. */
export default function IndividualStudentPicker(props: IndividualStudentPickerProps) {
    const [search, setSearch] = useState<string>('')
    const isValidGrade = !!props.gradeId && props.gradeId !== '-1'
    const studentsQuery = useStudentsList(
        { grade: isValidGrade ? Number(props.gradeId) : undefined },
        { enabled: isValidGrade }
    )
    const all = (studentsQuery.data?.results ?? [])
        .filter((s) => !props.sectionIds || (s.section != null && props.sectionIds.includes(s.section.id)))
    const q = search.trim().toLowerCase()
    const filtered = q
        ? all.filter((s) => `${s.first_name} ${s.last_name} ${s.username}`.toLowerCase().includes(q))
        : all
    const pg = usePagination(filtered, 10, q)
    if (!isValidGrade) {
        return (
            <div className="rounded-[14px] border border-line p-3 text-sm text-muted">
                Select a grade first.
            </div>
        )
    }
    return (
        <div>
            <label className="flex min-h-[44px] w-full items-center gap-2 rounded-[12px] border border-line-strong bg-surface px-3.5 focus-within:outline focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-accent">
                <Search size={18} strokeWidth={1.8} aria-hidden className="flex-none text-muted" />
                <span className="sr-only">Find students ({props.selectedIds.length} selected)</span>
                <input
                    id="exam-student-search"
                    type="search"
                    placeholder={`Find students in ${props.gradeLabel ?? 'this grade'}…`}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="min-w-0 flex-1 border-0 bg-transparent text-sm text-ink outline-none placeholder:text-muted"
                />
            </label>
            <div className="mt-2">
                {studentsQuery.isLoading ? (
                    <div className="p-3 text-sm text-muted">Loading students…</div>
                ) : filtered.length === 0 ? (
                    <div className="p-3 text-sm text-muted">No matching students.</div>
                ) : (
                    pg.pageItems.map((s) => {
                        const on = props.selectedIds.includes(s.id)
                        const name = `${s.first_name} ${s.last_name}`.trim() || s.username
                        return (
                            <div key={s.id} className="flex items-center gap-3 border-b border-hairline py-[11px] last:border-b-0">
                                <Initials name={name} />
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate font-semibold text-ink">{name}</span>
                                    <span className="text-xs text-muted">{s.section?.display ?? s.grade_name ?? s.username}</span>
                                </span>
                                <button
                                    type="button"
                                    aria-pressed={on}
                                    aria-label={`${on ? 'Remove' : 'Add'} ${name}`}
                                    onClick={() => props.onToggle(s.id)}
                                    className={`inline-flex min-h-[34px] items-center gap-1.5 rounded-[10px] border px-[11px] text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${on ? 'border-success/30 bg-success-bg text-success' : 'border-line-strong bg-surface text-ink hover:border-slate'}`}
                                >
                                    {on ? <Check size={14} strokeWidth={2} aria-hidden /> : <Plus size={14} strokeWidth={2} aria-hidden />}
                                    {on ? 'Added' : 'Add'}
                                </button>
                            </div>
                        )
                    })
                )}
            </div>
            <PagedFooter className="mt-2" from={pg.from} to={pg.to} total={pg.total} page={pg.page} pageCount={pg.pageCount} onPage={pg.setPage} label="Students pagination" />
            {props.selectedIds.length === 0 && (
                <p className="mt-2 text-xs text-danger">Pick at least one student.</p>
            )}
        </div>
    )
}
